import { createContext, useContext, type ReactNode } from "react";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

/**
 * Where the chat runs. The Report UI uses the defaults (everything on, full layout, in-app navigation);
 * the embedded chat narrows features per embed, uses a compact layout and opens app pages in a new tab.
 */
export type ChatFeatureFlags = {
  saveWidget: boolean;
  addToDashboard: boolean;
  mutations: boolean;
  export: boolean;
  warehouseJobs: boolean;
  forecast: boolean;
  showHistory: boolean;
  scopePickers: boolean;
  /** Embedded chatbot only. Report chat leaves this off. */
  formFill: boolean;
};

export type ChatHost = {
  mode: "app" | "embed";
  features: ChatFeatureFlags;
  /** Narrow panel (embedded chat): history as overlay, no page-fullscreen toggle. */
  compact: boolean;
  /** Replaces the empty-chat subtitle (embed welcome message). */
  welcomeMessage?: string;
  /** Initial OpenSearch index scope when the user has not picked one. */
  defaultIndices?: string[];
  /** Opens a Report UI page (e.g. /widgets/123/edit); undefined = in-app navigation. */
  openInApp?: (path: string) => void;
  /**
   * Embedded chat only: while the workspace setup is incomplete every message gets a standard reply
   * instead of being sent. Undefined (Report UI) = messages are always sent.
   */
  setupGate?: ChatSetupGate;
};

export type ChatSetupGate = {
  /** The standard reply while setup is incomplete; null when messages may be sent. */
  replyIfBlocked: () => string | null;
  /** The server refused a message because setup is incomplete (428); returns the reply to show. */
  onSetupRequired: () => string;
};

export const ALL_CHAT_FEATURES: ChatFeatureFlags = {
  saveWidget: true,
  addToDashboard: true,
  mutations: true,
  export: true,
  warehouseJobs: true,
  forecast: true,
  showHistory: true,
  scopePickers: true,
  formFill: false,
};

const APP_HOST: ChatHost = { mode: "app", features: ALL_CHAT_FEATURES, compact: false };

const ChatHostContext = createContext<ChatHost>(APP_HOST);

export function ChatHostProvider({ value, children }: { value: ChatHost; children: ReactNode }) {
  return <ChatHostContext.Provider value={value}>{children}</ChatHostContext.Provider>;
}

export function useChatHost(): ChatHost {
  return useContext(ChatHostContext);
}

/** Renders children only when the host allows the feature; otherwise a short "not available here" note. */
export function ChatFeatureGate({ feature, children }: { feature: keyof ChatFeatureFlags; children: ReactNode }) {
  const { features } = useChatHost();
  const { t } = useTranslation();
  if (features[feature]) return <>{children}</>;
  return (
    <Typography variant="body2" color="text.secondary">
      {t("chat.embed.featureDisabled")}
    </Typography>
  );
}
